import { getApiUrl } from '../utils/apiConfig';
import { getAuthHeaders } from '../utils/userUtils';


const PENDING_INVITE_KEY = 'whatquiz_pending_invite_v1';
const PENDING_INVITE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const INVITE_CODE_PATTERN = /^WQ[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{10}$/;
const VALID_CLAIM_STATUSES = new Set(['attributed', 'already_attributed']);
let pendingInviteMemory = null;

const terminalErrorCodes = new Set([
  'feature_disabled',
  'invalid_invite_code',
  'invite_code_not_found',
  'self_invite',
  'account_deleting',
  'account_not_new',
  'already_attributed',
]);

const normalizeCode = (value) => {
  const code = String(value || '').trim().toUpperCase();
  return INVITE_CODE_PATTERN.test(code) ? code : null;
};

const sanitizeSourcePath = (value) => {
  const path = String(value || '/').split('?', 1)[0].split('#', 1)[0];
  return path.startsWith('/') ? path.slice(0, 300) : '/';
};

const safeStorageGet = () => {
  try {
    return localStorage.getItem(PENDING_INVITE_KEY);
  } catch (_error) {
    return null;
  }
};

const safeStorageSet = (value) => {
  pendingInviteMemory = value;
  try {
    localStorage.setItem(PENDING_INVITE_KEY, JSON.stringify(value));
  } catch (_error) {
    // Safari private mode/용량 제한에서도 현재 탭의 메모리 fallback을 유지한다.
  }
};

const safeStorageRemove = () => {
  pendingInviteMemory = null;
  try {
    localStorage.removeItem(PENDING_INVITE_KEY);
  } catch (_error) {
    // 저장소가 차단되어도 전역 Bridge를 깨뜨리지 않는다.
  }
};

const safelyReadPendingInvite = (now = Date.now()) => {
  try {
    const stored = safeStorageGet();
    const parsed = stored ? JSON.parse(stored) : pendingInviteMemory;
    if (!parsed || typeof parsed !== 'object') return null;
    const code = normalizeCode(parsed.code);
    const capturedAt = Number(parsed.capturedAt);
    const expiresAt = Number(parsed.expiresAt);
    const validTimes = (
      Number.isFinite(capturedAt)
      && Number.isFinite(expiresAt)
      && capturedAt >= 0
      && capturedAt <= now + (5 * 60 * 1000)
      && expiresAt > now
      && expiresAt > capturedAt
      && expiresAt - capturedAt <= PENDING_INVITE_TTL_MS + 1000
    );
    if (!code || !validTimes) {
      safeStorageRemove();
      return null;
    }
    const pending = {
      code,
      sourcePath: sanitizeSourcePath(parsed.sourcePath),
      capturedAt,
      expiresAt,
    };
    pendingInviteMemory = pending;
    return pending;
  } catch (_error) {
    safeStorageRemove();
    return null;
  }
};

export const captureInviteAttribution = (
  search = window.location.search,
  pathname = window.location.pathname,
  now = Date.now(),
) => {
  const params = new URLSearchParams(search || '');
  const code = normalizeCode(params.get('ref'));
  if (!code) return safelyReadPendingInvite(now);

  const existing = safelyReadPendingInvite(now);
  // 추천인 가로채기를 막기 위해 유효한 최초 코드를 유지한다.
  if (existing) return existing;

  const pending = {
    code,
    sourcePath: sanitizeSourcePath(pathname),
    capturedAt: now,
    expiresAt: now + PENDING_INVITE_TTL_MS,
  };
  safeStorageSet(pending);
  return pending;
};

export const getPendingInviteAttribution = () => safelyReadPendingInvite();

export const clearPendingInviteAttribution = () => {
  safeStorageRemove();
};

const invalidResponse = (message = '초대 서버 응답을 확인하지 못했어요.') => {
  const error = new Error(message);
  error.code = 'invalid_invite_response';
  return error;
};

const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (response.ok) return data;

  const detail = data.detail;
  const code = typeof detail === 'object' ? detail?.code : null;
  const message = (
    (typeof detail === 'object' ? detail?.message : detail)
    || data.error
    || '초대 정보를 처리하지 못했어요.'
  );
  const error = new Error(message);
  error.status = response.status;
  error.code = code || 'invite_request_failed';
  throw error;
};

export const claimPendingInviteAttribution = async (user) => {
  const pending = safelyReadPendingInvite();
  if (!pending) return { status: 'no_pending_invite', terminal: true };
  if (!user?.getIdToken || user?.isTempReviewUser) {
    return { status: 'not_authenticated', terminal: false };
  }

  try {
    const headers = await getAuthHeaders(user);
    const response = await fetch(getApiUrl('/api/invites/claim'), {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        invite_code: pending.code,
        source_path: pending.sourcePath,
      }),
    });
    const result = await parseResponse(response);
    if (
      result.success !== true
      || !VALID_CLAIM_STATUSES.has(result.status)
      || typeof result.attributed !== 'boolean'
    ) {
      throw invalidResponse();
    }
    clearPendingInviteAttribution();
    return { ...result, terminal: true };
  } catch (error) {
    const terminal = terminalErrorCodes.has(error.code);
    if (terminal) clearPendingInviteAttribution();
    return {
      success: false,
      status: error.code || 'invite_claim_failed',
      error: error.message,
      terminal,
    };
  }
};

export const getMyInviteLink = async (user) => {
  if (!user?.getIdToken || user?.isTempReviewUser) {
    throw new Error('로그인 후 초대 링크를 만들 수 있어요.');
  }

  const headers = await getAuthHeaders(user);
  const response = await fetch(getApiUrl('/api/invites/me'), {
    headers,
  });
  const result = await parseResponse(response);
  const code = normalizeCode(result.invite_code);
  if (result.success !== true || !code || typeof result.share_url !== 'string') {
    throw invalidResponse('초대 링크 응답을 확인하지 못했어요.');
  }

  let shareUrl;
  try {
    shareUrl = new URL(result.share_url);
  } catch (_error) {
    throw invalidResponse('초대 링크 주소를 확인하지 못했어요.');
  }
  const localHttp = (
    shareUrl.protocol === 'http:'
    && ['localhost', '127.0.0.1'].includes(shareUrl.hostname)
  );
  if (
    (shareUrl.protocol !== 'https:' && !localHttp)
    || normalizeCode(shareUrl.searchParams.get('ref')) !== code
  ) {
    throw invalidResponse('초대 링크 주소를 확인하지 못했어요.');
  }
  return { ...result, invite_code: code, share_url: shareUrl.toString() };
};

export const getInviteProgram = async () => {
  const response = await fetch(getApiUrl('/api/invites/program'));
  const result = await parseResponse(response);
  if (
    result.success !== true
    || typeof result.program_enabled !== 'boolean'
  ) {
    throw invalidResponse('초대 혜택 정보를 확인하지 못했어요.');
  }
  return result;
};

export const getInviteAdminSummary = async (user) => {
  if (!user?.getIdToken || user?.isTempReviewUser) {
    throw new Error('관리자 로그인이 필요해요.');
  }
  const headers = await getAuthHeaders(user);
  const response = await fetch(getApiUrl('/api/invites/admin/summary'), {
    headers,
  });
  const result = await parseResponse(response);
  if (
    result.success !== true
    || typeof result.funnel !== 'object'
    || typeof result.rewards !== 'object'
  ) {
    throw invalidResponse('초대 운영 지표를 확인하지 못했어요.');
  }
  return result;
};

export const attachInviteAttributionToUrl = (
  targetUrl,
  inviteShareUrl,
  surface = '',
) => {
  try {
    const target = new URL(targetUrl, window.location.origin);
    const invite = new URL(inviteShareUrl);
    const code = normalizeCode(invite.searchParams.get('ref'));
    const sameTrustedOrigin = (
      target.origin === invite.origin
      || target.origin === window.location.origin
    );
    if (
      !code
      || !sameTrustedOrigin
      || !['http:', 'https:'].includes(target.protocol)
    ) {
      return String(targetUrl || '');
    }

    target.searchParams.set('ref', code);
    const safeSurface = String(surface || '')
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, '')
      .slice(0, 40);
    if (safeSurface) target.searchParams.set('ref_surface', safeSurface);
    return target.toString();
  } catch (_error) {
    return String(targetUrl || '');
  }
};

export const prepareTeacherReferralShareUrl = async ({
  user,
  url,
  surface,
  audience,
}) => {
  const originalUrl = String(url || '');
  if (
    audience !== 'teacher'
    || !canUseInviteFeature(user)
    || !originalUrl
  ) {
    return { url: originalUrl, referralAttached: false };
  }

  try {
    const invite = await getMyInviteLink(user);
    const referralUrl = attachInviteAttributionToUrl(
      originalUrl,
      invite.share_url,
      surface,
    );
    return {
      url: referralUrl,
      referralAttached: referralUrl !== originalUrl,
    };
  } catch (_error) {
    // 추천 링크 준비 실패가 사용자의 원래 공유를 막으면 안 된다.
    return { url: originalUrl, referralAttached: false };
  }
};

export const isInviteUiEnabled = () => (
  process.env.REACT_APP_INVITE_UI_ENABLED === 'true'
  || process.env.NODE_ENV === 'development'
);

export const canUseInviteFeature = (user) => (
  isInviteUiEnabled() && !user?.isTempReviewUser
);

export const inviteAttributionConstants = {
  PENDING_INVITE_KEY,
  PENDING_INVITE_TTL_MS,
};
